import { useState, useCallback, useEffect } from 'react';
import { listHistoricalSessions } from '../lib/firebase.js';
import { groupSessionsByBoat } from '../lib/sessionGrouping.js';
import { defaultDateRange, sessionRow } from '../lib/sessionPicker.js';

// Modal that browses recorded sessions (sail_no → device → session, the same
// shape as the Live Activity dashboard) so an admin can pick a track to overlay
// on candidate courses. The events/ tree is never read client-side — the
// admin-only listHistoricalSessions callable does the walk (ra-97jo).

const STATUS_STYLE = {
  active: { label: 'active', bg: '#198754', fg: '#fff' },
  finished: { label: 'finished', bg: '#e0e0e0', fg: '#444' },
  unknown: { label: 'unknown', bg: '#e0e0e0', fg: '#444' },
};

function StatusPill({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.unknown;
  return (
    <span
      style={{
        fontSize: '0.66rem',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.03em',
        padding: '0.1rem 0.4rem',
        borderRadius: 3,
        background: s.bg,
        color: s.fg,
        whiteSpace: 'nowrap',
      }}
    >
      {s.label}
    </span>
  );
}

function SessionLine({ session, busy, onPick }) {
  const row = sessionRow(session);
  return (
    <button
      type="button"
      className="session-pick-row"
      disabled={busy}
      onClick={() => onPick(session)}
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '0.4rem 1rem',
        width: '100%',
        textAlign: 'left',
        background: 'none',
        border: 'none',
        borderTop: '1px solid #f1f3f5',
        padding: '0.45rem 0.2rem',
        cursor: busy ? 'wait' : 'pointer',
        fontSize: '0.82rem',
        color: '#444',
      }}
    >
      <StatusPill status={row.status} />
      <span><strong style={{ color: '#1a1a2e' }}>{row.date}</strong> {row.time}</span>
      <span><strong style={{ color: '#1a1a2e' }}>Course:</strong> {row.courseName || '—'}</span>
      <span>{row.fleetClass}</span>
      {row.durationMin != null && <span>{row.durationMin}m</span>}
      <span style={{ flex: 1 }} />
      <span style={{ color: '#3b6ef0', fontWeight: 600 }}>Overlay →</span>
    </button>
  );
}

function BoatGroup({ boat, busy, onPick }) {
  const [open, setOpen] = useState(false);
  // Flatten to a plain session list when there's 0–1 device, otherwise show the
  // device sub-groups (a session with several devices appears under each — the
  // server merges all devices' fixes regardless of which row is picked).
  const flatten = boat.deviceCount <= 1;
  const flatSessions = flatten
    ? [
        ...boat.deviceGroups.flatMap((g) => g.entries.map((e) => e.session)),
        ...boat.sessionsWithoutDevices,
      ]
    : [];

  return (
    <div
      style={{
        border: '1px solid #e0e0e0',
        borderRadius: 8,
        background: '#fff',
        marginBottom: '0.5rem',
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        style={{
          width: '100%',
          textAlign: 'left',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '0.7rem 0.9rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.6rem',
        }}
      >
        <span style={{ color: '#888', width: '0.8rem' }}>{open ? '▾' : '▸'}</span>
        <span style={{ fontSize: '1.02rem', fontWeight: 700, color: '#1a1a2e' }}>{boat.sailNo}</span>
        <span style={{ fontSize: '0.8rem', color: '#444' }}>{boat.classes.join(', ') || '—'}</span>
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: '0.78rem', color: '#777' }}>
          {boat.sessionCount} session{boat.sessionCount === 1 ? '' : 's'}
        </span>
      </button>

      {open && (
        <div style={{ padding: '0 0.9rem 0.7rem', borderTop: '1px solid #f1f3f5' }}>
          {flatten
            ? flatSessions.map((s) => (
                <SessionLine key={s.sessionPath} session={s} busy={busy} onPick={onPick} />
              ))
            : boat.deviceGroups.map((g) => (
                <div key={g.deviceId} style={{ marginTop: '0.5rem' }}>
                  <code
                    style={{
                      background: '#f5f5f7',
                      padding: '0.1rem 0.4rem',
                      borderRadius: 3,
                      fontSize: '0.72rem',
                      color: '#666',
                    }}
                  >
                    {(g.deviceId || '?').slice(0, 12)}
                  </code>
                  {g.entries.map(({ session }) => (
                    <SessionLine
                      key={session.sessionPath}
                      session={session}
                      busy={busy}
                      onPick={onPick}
                    />
                  ))}
                </div>
              ))}
        </div>
      )}
    </div>
  );
}

export default function SessionPicker({ onSelect, onClose }) {
  const initial = defaultDateRange();
  const [fromDate, setFromDate] = useState(initial.fromDate);
  const [toDate, setToDate] = useState(initial.toDate);
  const [boats, setBoats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [picking, setPicking] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    setBoats(null);
    try {
      const res = await listHistoricalSessions(fromDate, toDate);
      setBoats(groupSessionsByBoat(res.sessions || [], Date.now()));
    } catch (err) {
      setError(err?.message || 'Failed to load sessions.');
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  const handlePick = useCallback(
    async (session) => {
      setPicking(true);
      setError('');
      try {
        await onSelect(session);
      } catch (err) {
        setError(err?.message || 'Failed to load track.');
      } finally {
        setPicking(false);
      }
    },
    [onSelect],
  );

  // Close on Escape for keyboard users.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="session-picker-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="Browse recorded sessions"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '3rem 1rem',
      }}
    >
      <div
        className="session-picker"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fafafa',
          borderRadius: 10,
          width: 'min(640px, 100%)',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.6rem',
            padding: '1rem 1.2rem',
            borderBottom: '1px solid #e6e6e6',
          }}
        >
          <h2 style={{ margin: 0, fontSize: '1.1rem' }}>Browse recorded sessions</h2>
          <span style={{ flex: 1 }} />
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{ border: 'none', background: 'none', fontSize: '1.3rem', cursor: 'pointer', color: '#666' }}
          >
            ×
          </button>
        </div>

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-end',
            gap: '0.8rem',
            padding: '0.9rem 1.2rem',
            borderBottom: '1px solid #eee',
          }}
        >
          <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.74rem', color: '#666' }}>
            From
            <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.74rem', color: '#666' }}>
            To
            <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
          </label>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            style={{
              padding: '6px 16px',
              border: '1px solid #3b6ef0',
              background: '#3b6ef0',
              color: '#fff',
              borderRadius: 5,
              cursor: loading ? 'wait' : 'pointer',
              fontWeight: 600,
            }}
          >
            {loading ? 'Loading…' : 'Load sessions'}
          </button>
        </div>

        <div style={{ overflowY: 'auto', padding: '0.9rem 1.2rem' }}>
          {error && <div className="error-text" style={{ marginBottom: '0.6rem' }}>{error}</div>}
          {picking && <p style={{ color: '#666' }}>Loading track…</p>}
          {boats == null && !loading && !error && (
            <p className="empty-state" style={{ margin: 0 }}>
              Pick a date range and load sessions to overlay a recorded track.
            </p>
          )}
          {boats != null && boats.length === 0 && (
            <p className="empty-state" style={{ margin: 0 }}>No recorded sessions in that range.</p>
          )}
          {boats != null &&
            boats.map((boat) => (
              <BoatGroup key={boat.sailNo} boat={boat} busy={picking} onPick={handlePick} />
            ))}
        </div>
      </div>
    </div>
  );
}
